'use client';

/**
 * Compact "points" pill shown next to {@code NotificationsBell} —
 * used by every header style with {@code header.showGamificationPoints:
 * true} in shell config.
 *
 * <h3>Host adapter</h3>
 * Data comes from {@link useGamificationSource}. When no source is
 * mounted (host hasn't wired gamification yet), the widget renders
 * nothing — same "no inert affordance" convention as
 * {@code NotificationsBell}.
 */
import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import type { CSSProperties } from 'react';
import {
  GAMIFICATION_REFRESH_EVENT,
  useGamificationSource,
  useLocale,
  useMessage,
} from '../../portal';

export interface GamificationPointsBadgeProps {
  /** Light icon styling for dark-chrome headers. */
  inverse?: boolean;
}

/** Built-in fallback for the point-unit word, below the host's
 *  {@code theme.*}-style message override and below the API's own
 *  {@code pointName} (e.g. tenant-configured "XP"). */
function tr(locale: string): string {
  const dict: Record<string, string> = { tr: 'Puan', en: 'Points' };
  return dict[locale] ?? dict['en']!;
}

export function GamificationPointsBadge({ inverse }: GamificationPointsBadgeProps) {
  const source = useGamificationSource();
  const { locale } = useLocale();
  const defaultPointName = useMessage('gamification.pointName', tr(locale));
  const [points, setPoints] = useState<number | null>(null);
  const [iconUrl, setIconUrl] = useState<string | undefined>(undefined);
  const [pointName, setPointName] = useState<string | undefined>(undefined);

  const refetch = useCallback(
    (signal?: AbortSignal) => {
      if (!source) return;
      source
        .fetchSummary(signal)
        .then((summary) => {
          if (signal?.aborted) return;
          setPoints(summary.points);
          setIconUrl(summary.iconUrl);
          setPointName(summary.pointName);
        })
        .catch(() => {
          /* swallow — pill just stays hidden */
        });
    },
    [source],
  );

  useEffect(() => {
    const ac = new AbortController();
    refetch(ac.signal);
    return () => ac.abort();
  }, [refetch]);

  useEffect(() => {
    if (!source) return;
    const onRefresh = () => refetch();
    window.addEventListener(GAMIFICATION_REFRESH_EVENT, onRefresh);
    return () => window.removeEventListener(GAMIFICATION_REFRESH_EVENT, onRefresh);
  }, [source, refetch]);

  // No source mounted, or the fetch hasn't resolved yet — don't show
  // an inert/empty pill.
  if (!source || points === null) return null;

  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 36,
    padding: '0 12px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 600,
    color: inverse ? 'var(--color-text-on-sunken, #ffffff)' : 'var(--color-text-primary, #18181b)',
    border: inverse
      ? '1px solid var(--color-border-on-sunken, rgba(255,255,255,0.18))'
      : '1px solid var(--color-border-subtle, rgba(0,0,0,0.08))',
    textDecoration: 'none',
  };

  return (
    <Link href="/lxp/gamification" style={style}>
      {iconUrl ? (
        <img src={iconUrl} alt="" width={16} height={16} style={{ objectFit: 'contain' }} />
      ) : (
        <StarIcon />
      )}
      <span>
        {points.toLocaleString()} {pointName ?? defaultPointName}
      </span>
    </Link>
  );
}

function StarIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      aria-hidden
      style={{ color: 'var(--color-warning-fg, #f59e0b)' }}
    >
      <path d="M12 2.5l2.9 6.1 6.6.6-5 4.5 1.5 6.5-6-3.6-6 3.6 1.5-6.5-5-4.5 6.6-.6z" />
    </svg>
  );
}
